import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FlashcardService, Flashcard, FlashcardCategory } from './flashcard.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { FileUploadModule } from 'primeng/fileupload';
import { BadgeModule } from 'primeng/badge';
import { EditorModule } from 'primeng/editor';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
  selector: 'app-flashcard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    FileUploadModule,
    BadgeModule,
    EditorModule,
    ToggleSwitchModule
  ],
  template: `
    <p-toast></p-toast>
    <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
    <div class="card p-6 md:p-8 bg-white border border-slate-100 rounded-3xl shadow-xs">
      <!-- Top Action Bar -->
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-5 border-b border-slate-100">
        <div>
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-blue-50 text-[#3B82F6] flex items-center justify-center text-base font-bold">
              <i class="pi pi-layers"></i>
            </div>
            <h4 class="text-xl font-black text-slate-800 m-0 tracking-tight">Kelola Flashcard</h4>
          </div>
          <p class="text-xs text-slate-500 m-0 mt-1 pl-11">Total <strong class="text-blue-600">{{ totalRecords }}</strong> kartu belajar tersedia</p>
        </div>

        <div class="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <!-- Filter Category -->
          <p-select
            [options]="categories"
            [(ngModel)]="selectedFilterCategoryId"
            optionLabel="name"
            optionValue="id"
            placeholder="Semua Kategori"
            [showClear]="true"
            [filter]="true"
            filterBy="name"
            styleClass="w-56 p-select-sm">
          </p-select>

          <!-- Search keyword -->
          <span class="relative flex-1 md:flex-initial">
            <input pInputText type="text" (input)="onInput($event, dt)" placeholder="Cari kata kunci..." class="w-full md:w-56 p-inputtext-sm" />
          </span>

          <!-- Add Button -->
          <p-button label="Tambah Flashcard" icon="pi pi-plus" (onClick)="addFlashcard()" severity="primary" size="small" styleClass="shadow-sm font-bold"></p-button>
        </div>
      </div>

      <!-- Flashcard Data Table -->
      <p-table #dt [value]="displayFlashcards" [(first)]="first" [lazy]="true" (onLazyLoad)="onLazyLoad($event)" [totalRecords]="totalRecords" [loading]="loadingTable" [paginator]="true" [rows]="rows" rowGroupMode="subheader" groupRowsBy="category_name" sortField="category_name" [sortOrder]="1" [tableStyle]="{ 'min-width': '50rem' }" styleClass="p-datatable-sm">
        <ng-template pTemplate="header">
          <tr class="bg-slate-50/90 text-slate-700 text-xs font-bold uppercase tracking-wider border-b border-slate-200">
            <th style="width: 180px;" class="py-3 px-4">Topik</th>
            <th class="py-3 px-4">Pertanyaan / Soal</th>
            <th class="py-3 px-4">Jawaban / Penjelasan</th>
            <th style="width: 110px;" class="py-3 px-4 text-center">Tipe</th>
            <th style="width: 100px;" class="py-3 px-4 text-center">Aksi</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="groupheader" let-flashcard>
          <tr pRowGroupHeader class="bg-gradient-to-r from-blue-50/90 via-indigo-50/40 to-transparent border-y border-blue-100">
            <td colspan="5" class="py-2.5 px-4">
              <div class="flex items-center gap-2">
                <i class="pi pi-folder text-blue-600 text-sm"></i>
                <span class="font-extrabold text-xs md:text-sm text-blue-900 tracking-wide uppercase">{{ flashcard.category_name }}</span>
                <span class="bg-blue-200/70 text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full ml-1 shadow-2xs">
                  {{ flashcard.category_card_count ? (flashcard.category_card_count + ' Kartu') : (getCategoryCardCount(flashcard.category_name) + ' Kartu') }}
                </span>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-flashcard>
          <tr class="hover:bg-blue-50/25 transition-colors border-b border-slate-100 text-slate-800 text-sm">
            <!-- Topik -->
            <td class="py-3.5 px-4 font-semibold text-slate-700 align-top">
              <span class="inline-block line-clamp-2">{{ flashcard.topic || '-' }}</span>
            </td>

            <!-- Pertanyaan -->
            <td class="py-3.5 px-4 text-slate-800 leading-relaxed align-top">
              <div class="line-clamp-2 text-slate-800 font-medium">
                {{ getCleanSnippet(flashcard.text) || '-' }}
              </div>
            </td>

            <!-- Jawaban -->
            <td class="py-3.5 px-4 text-slate-600 leading-relaxed align-top">
              <div *ngIf="flashcard.description" class="line-clamp-2 text-slate-600">
                {{ getCleanSnippet(flashcard.description) }}
              </div>
              <div *ngIf="!flashcard.description && flashcard.type === 'image'" class="text-xs text-blue-600/90 font-semibold italic flex items-center gap-1">
                <i class="pi pi-image text-[11px]"></i> Label Area Gambar
              </div>
              <div *ngIf="!flashcard.description && flashcard.type !== 'image'" class="text-xs text-slate-400 italic">
                -
              </div>
            </td>

            <!-- Tipe Badge -->
            <td class="py-3.5 px-4 text-center align-top whitespace-nowrap">
              <span *ngIf="flashcard.type === 'text' || !flashcard.type" class="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200/80 rounded-full text-xs font-bold shadow-2xs">
                <i class="pi pi-align-left text-[10px]"></i>
                <span>Text</span>
              </span>

              <span *ngIf="flashcard.type === 'image'" class="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/80 rounded-full text-xs font-bold shadow-2xs">
                <i class="pi pi-image text-[10px]"></i>
                <span>Image</span>
              </span>

              <span *ngIf="flashcard.type === 'cloze'" class="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 border border-purple-200/80 rounded-full text-xs font-bold shadow-2xs">
                <i class="pi pi-file-edit text-[10px]"></i>
                <span>Cloze</span>
              </span>
            </td>

            <!-- Action Buttons Horizontal Row -->
            <td class="py-3.5 px-4 text-center align-top whitespace-nowrap">
              <div class="flex items-center justify-center gap-1.5">
                <p-button icon="pi pi-pencil" [rounded]="true" [outlined]="true" severity="info" size="small" (onClick)="editFlashcard(flashcard)" title="Edit"></p-button>
                <p-button icon="pi pi-trash" [rounded]="true" [outlined]="true" severity="danger" size="small" (onClick)="deleteFlashcard(flashcard)" title="Hapus"></p-button>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="text-center py-12 text-slate-400">
              <div class="flex flex-col items-center justify-center gap-2">
                <i class="pi pi-inbox text-4xl text-slate-300"></i>
                <span class="font-medium text-slate-600">Tidak ada data flashcard ditemukan</span>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Dialog Create/Edit Flashcard -->
      <p-dialog [(visible)]="displayDialog" [style]="{ width: '92vw', maxWidth: '1150px' }" [modal]="true" [dismissableMask]="false" [draggable]="false" class="p-fluid">
        <ng-template pTemplate="header">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-blue-50 text-[#3B82F6] flex items-center justify-center text-lg font-bold border border-blue-100 shadow-2xs">
              <i [class]="isEditMode ? 'pi pi-pencil' : 'pi pi-plus'"></i>
            </div>
            <div>
              <h3 class="text-lg md:text-xl font-extrabold text-slate-800 m-0">
                {{ isEditMode ? 'Edit Flashcard' : 'Tambah Flashcard Baru' }}
              </h3>
              <p class="text-xs text-slate-500 m-0 mt-0.5">Lengkapi formulir di bawah ini untuk mengatur kartu belajar</p>
            </div>
          </div>
        </ng-template>

        <ng-template pTemplate="content">
          <form [formGroup]="flashcardForm" class="flex flex-col gap-4 py-2">
            
            <!-- 1. Metadata Grid (Kategori, Tipe, Topik) -->
            <div class="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 md:p-5">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- 1.1 Kategori -->
                <div>
                  <label for="flashcard_category_id" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i class="pi pi-folder text-blue-500"></i>
                    Kategori <span class="text-red-500">*</span>
                  </label>
                  <p-select
                    id="flashcard_category_id"
                    formControlName="flashcard_category_id"
                    [options]="categories"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Pilih Kategori"
                    [filter]="true"
                    filterBy="name"
                    filterPlaceholder="Cari kategori..."
                    class="w-full">
                  </p-select>
                </div>

                <!-- 1.2 Tipe Flashcard -->
                <div>
                  <label for="type" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i class="pi pi-th-large text-blue-500"></i>
                    Tipe Kartu <span class="text-red-500">*</span>
                  </label>
                  <p-select
                    id="type"
                    formControlName="type"
                    [options]="[
                      { label: 'Teks (Pertanyaan & Jawaban)', value: 'text', icon: 'pi pi-align-left text-blue-600' },
                      { label: 'Gambar (Occlusion / Diagram)', value: 'image', icon: 'pi pi-image text-emerald-600' },
                      { label: 'Cloze (Kalimat Rumpang)', value: 'cloze', icon: 'pi pi-file-edit text-purple-600' }
                    ]"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Pilih Tipe Kartu"
                    class="w-full">
                    <ng-template pTemplate="selectedItem" let-selectedOption>
                      <div class="flex items-center gap-2" *ngIf="selectedOption">
                        <i [class]="selectedOption.icon || (selectedOption.value === 'image' ? 'pi pi-image text-emerald-600' : selectedOption.value === 'cloze' ? 'pi pi-file-edit text-purple-600' : 'pi pi-align-left text-blue-600')"></i>
                        <span>{{ selectedOption.label }}</span>
                      </div>
                    </ng-template>
                    <ng-template pTemplate="item" let-option>
                      <div class="flex items-center gap-2">
                        <i [class]="option.icon"></i>
                        <span>{{ option.label }}</span>
                      </div>
                    </ng-template>
                  </p-select>
                </div>

                <!-- 1.3 Topik -->
                <div>
                  <label for="topic" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i class="pi pi-bookmark text-blue-500"></i>
                    Topik / Judul <span class="text-red-500">*</span>
                  </label>
                  <input pInputText id="topic" formControlName="topic" class="w-full" placeholder="Contoh: Anatomi Jantung" />
                </div>
              </div>
            </div>

            <!-- CASE A: For Text and Cloze Flashcards (Clean 2-Column Grid) -->
            <div *ngIf="flashcardForm.get('type')?.value !== 'image'" class="grid grid-cols-1 lg:grid-cols-2 gap-5">
              
              <!-- LEFT COLUMN: PERTANYAAN -->
              <div class="bg-white border border-slate-200/90 rounded-2xl p-4 md:p-5 flex flex-col justify-between shadow-2xs">
                <div class="space-y-3">
                  <div class="flex items-center justify-between border-b border-slate-100 pb-2.5">
                    <span class="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <i class="pi pi-question-circle text-blue-600"></i>
                      Pertanyaan / Soal
                    </span>
                    <span class="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-md">Wajib</span>
                  </div>

                  <!-- Cloze Hint -->
                  <div *ngIf="flashcardForm.get('type')?.value === 'cloze'" class="bg-blue-50/80 border border-blue-200/80 rounded-xl p-2.5 text-xs text-blue-900 leading-relaxed flex items-start gap-2">
                    <i class="pi pi-info-circle text-blue-600 mt-0.5"></i>
                    <span>
                      Gunakan kurung siku <strong>[...]</strong> atau <strong>[_____]</strong> pada kata/kalimat yang ingin dijadikan tebakan rumpang.
                    </span>
                  </div>

                  <p-editor id="text" formControlName="text" [style]="{'height':'150px'}" placeholder="Tulis pertanyaan di sini..."></p-editor>
                </div>

                <!-- Attachment: Gambar Pertanyaan -->
                <div class="mt-4 pt-3 border-t border-slate-100">
                  <label class="block text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                    <i class="pi pi-image text-slate-400"></i>
                    Lampiran Gambar Pertanyaan (Opsional)
                  </label>
                  <p-fileUpload
                    #questionImageUpload
                    mode="basic"
                    [multiple]="true"
                    accept="image/*"
                    maxFileSize="1000000"
                    (onSelect)="onQuestionImageSelect($event)"
                    chooseLabel="Pilih Gambar Pertanyaan"
                    styleClass="p-button-outlined p-button-sm">
                  </p-fileUpload>
                  
                  <!-- Existing Question Images Thumbnails -->
                  <div *ngIf="isEditMode && editingFlashcard && getExistingQuestionImages().length > 0" class="mt-2.5">
                    <span class="block text-[11px] font-semibold text-slate-500 mb-1.5">Gambar Tersimpan:</span>
                    <div class="flex flex-wrap gap-2">
                      <div *ngFor="let img of getExistingQuestionImages(); let idx = index" class="relative group border border-slate-200 rounded-xl overflow-hidden w-16 h-16 bg-slate-50 flex items-center justify-center shadow-2xs">
                        <img [src]="getImageUrl(img.image_path)" class="w-full h-full object-cover" />
                        <button type="button" class="absolute inset-0 bg-red-600/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border-none cursor-pointer text-white" (click)="deleteExistingQuestionImage(img, idx)" title="Hapus gambar">
                          <i class="pi pi-trash text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Selected New Question Files -->
                  <div *ngIf="selectedQuestionImageFiles.length > 0" class="mt-2">
                    <div *ngFor="let file of selectedQuestionImageFiles; let idx = index" class="flex items-center justify-between bg-blue-50 text-blue-800 text-xs px-2.5 py-1.5 rounded-lg border border-blue-100 mt-1">
                      <span class="truncate max-w-[200px]">{{ file.name }}</span>
                      <button type="button" class="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer p-0 ml-1" (click)="removeQuestionFile(idx)">
                        <i class="pi pi-times text-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- RIGHT COLUMN: JAWABAN -->
              <div class="bg-white border border-slate-200/90 rounded-2xl p-4 md:p-5 flex flex-col justify-between shadow-2xs">
                <div class="space-y-3">
                  <div class="flex items-center justify-between border-b border-slate-100 pb-2.5">
                    <span class="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <i class="pi pi-check-circle text-emerald-600"></i>
                      Jawaban / Penjelasan
                    </span>
                    <span class="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-md">Wajib</span>
                  </div>

                  <!-- If Cloze type: Simple text input for answer key -->
                  <ng-container *ngIf="flashcardForm.get('type')?.value === 'cloze'; else textAnswerEditor">
                    <div class="py-2">
                      <label for="description" class="block text-xs font-semibold text-slate-600 mb-1.5">Kunci Jawaban Rumpang</label>
                      <input pInputText id="description" formControlName="description" class="w-full" placeholder="Ketik kata/kunci jawaban... Contoh: Ventrikel Kiri" />
                      <small class="text-slate-400 mt-1 block">Kata ini yang akan menggantikan tanda kurung rumpang saat kartu dibalik.</small>
                    </div>
                  </ng-container>

                  <!-- If Regular Text type: Rich Text Editor -->
                  <ng-template #textAnswerEditor>
                    <p-editor id="description" formControlName="description" [style]="{'height':'150px'}" placeholder="Tulis jawaban lengkap / pembahasan di sini..."></p-editor>
                  </ng-template>
                </div>

                <!-- Attachment: Gambar Jawaban -->
                <div class="mt-4 pt-3 border-t border-slate-100">
                  <label class="block text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                    <i class="pi pi-image text-slate-400"></i>
                    Lampiran Gambar Jawaban (Opsional)
                  </label>
                  <p-fileUpload
                    #imageUpload
                    mode="basic"
                    [multiple]="true"
                    accept="image/*"
                    maxFileSize="1000000"
                    (onSelect)="onFileSelect($event)"
                    chooseLabel="Pilih Gambar Jawaban"
                    styleClass="p-button-outlined p-button-sm">
                  </p-fileUpload>

                  <!-- Existing Answer Images -->
                  <div *ngIf="isEditMode && editingFlashcard && getExistingAnswerImages().length > 0" class="mt-2.5">
                    <span class="block text-[11px] font-semibold text-slate-500 mb-1.5">Gambar Tersimpan:</span>
                    <div class="flex flex-wrap gap-2">
                      <div *ngFor="let img of getExistingAnswerImages(); let idx = index" class="relative group border border-slate-200 rounded-xl overflow-hidden w-16 h-16 bg-slate-50 flex items-center justify-center shadow-2xs">
                        <ng-container *ngIf="img.image_path.toLowerCase().endsWith('.pdf'); else isAnsImg">
                          <div class="flex flex-col items-center justify-center w-full h-full text-red-600 bg-red-50">
                            <i class="pi pi-file-pdf text-lg"></i>
                            <span class="text-[8px] font-bold">PDF</span>
                          </div>
                        </ng-container>
                        <ng-template #isAnsImg>
                          <img [src]="getImageUrl(img.image_path)" class="w-full h-full object-cover" />
                        </ng-template>
                        <button type="button" class="absolute inset-0 bg-red-600/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border-none cursor-pointer text-white" (click)="deleteExistingAnswerImage(img, idx)" title="Hapus gambar">
                          <i class="pi pi-trash text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Selected New Answer Files -->
                  <div *ngIf="selectedFiles.length > 0" class="mt-2">
                    <div *ngFor="let file of selectedFiles; let idx = index" class="flex items-center justify-between bg-emerald-50 text-emerald-800 text-xs px-2.5 py-1.5 rounded-lg border border-emerald-100 mt-1">
                      <span class="truncate max-w-[200px]">{{ file.name }}</span>
                      <button type="button" class="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer p-0 ml-1" (click)="removeAnswerFile(idx)">
                        <i class="pi pi-times text-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <!-- CASE B: For Image (Occlusion) Flashcards (Interactive Workspace) -->
            <div *ngIf="flashcardForm.get('type')?.value === 'image'" class="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-white border border-slate-200/90 rounded-2xl p-5 shadow-2xs">
              
              <!-- Left Column: Controls & Label List (span 5) -->
              <div class="lg:col-span-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1">
                <!-- Pertanyaan Text -->
                <div>
                  <label for="text" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Instruksi Pertanyaan <span class="text-red-500">*</span>
                  </label>
                  <input pInputText id="text" formControlName="text" class="w-full" placeholder="Contoh: Sebutkan bagian diagram jantung berikut" />
                </div>

                <!-- Upload Diagram Image -->
                <div class="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5">
                  <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    File Diagram Gambar <span class="text-red-500">*</span>
                  </label>
                  <p-fileUpload
                    #questionImageUpload
                    mode="basic"
                    [multiple]="false"
                    accept="image/*"
                    maxFileSize="2000000"
                    (onSelect)="onQuestionImageSelect($event)"
                    chooseLabel="Unggah Diagram Gambar"
                    styleClass="p-button-primary p-button-sm w-full">
                  </p-fileUpload>
                  <small class="text-slate-400 block mt-1">Maksimal 2MB, format JPG/PNG</small>
                </div>

                <!-- Occlusion Area Labels List -->
                <div>
                  <div class="flex items-center justify-between mb-2">
                    <label class="block text-xs font-bold text-slate-700 uppercase tracking-wider m-0">
                      Daftar Label Jawaban ({{ occlusions.length }})
                    </label>
                    <span *ngIf="occlusions.length > 0" class="text-[11px] text-blue-600 font-semibold">Tersimpan otomatis</span>
                  </div>

                  <div class="flex flex-col gap-2.5">
                    <div *ngFor="let rect of occlusions; let idx = index" class="flex gap-2 items-center bg-slate-50 p-2 rounded-xl border border-slate-200/80">
                      <span class="inline-flex items-center justify-center w-7 h-7 bg-blue-600 text-white text-xs font-extrabold rounded-lg shrink-0 shadow-2xs">
                        {{ idx + 1 }}
                      </span>
                      <input pInputText 
                             [(ngModel)]="rect.label" 
                             [ngModelOptions]="{standalone: true}" 
                             (ngModelChange)="updateFormValidityAndData()" 
                             class="flex-1 p-inputtext-sm" 
                             placeholder="Ketik nama label... Contoh: Ventrikel Kiri" />
                      <button type="button" 
                              class="text-red-500 hover:text-red-700 bg-transparent hover:bg-red-50 border-none cursor-pointer p-1.5 rounded-lg flex items-center justify-center transition-colors shrink-0" 
                              (click)="removeOcclusion(idx)"
                              title="Hapus area ini">
                        <i class="pi pi-trash text-sm"></i>
                      </button>
                    </div>

                    <div *ngIf="occlusions.length === 0" class="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4 text-center leading-relaxed">
                      <i class="pi pi-info-circle text-blue-500 text-base mb-1 block"></i>
                      Belum ada area label. Unggah gambar, lalu <strong>klik & seret mouse</strong> pada gambar di sebelah kanan untuk membuat kotak penutup.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column: Interactive Diagram Board (span 7) -->
              <div class="lg:col-span-7 flex flex-col items-center justify-center border-t lg:border-t-0 lg:border-l border-slate-200/80 pt-4 lg:pt-0 lg:pl-6">
                <div class="w-full flex items-center justify-between mb-2">
                  <span class="text-xs font-bold text-slate-700 uppercase tracking-wider">Papan Gambar & Area Occlusion</span>
                  <small class="text-slate-400">Klik & seret untuk menandai area</small>
                </div>

                <div *ngIf="previewImageSrc" class="relative border border-slate-200 rounded-2xl overflow-hidden bg-[#F8FAFC] flex items-center justify-center shadow-inner select-none mx-auto w-full" style="max-height: 480px;">
                  <img #previewImg [src]="previewImageSrc" class="block cursor-crosshair select-none max-h-[460px] w-auto max-w-full object-contain" (mousedown)="startSelect($event)" />
                  
                  <!-- Occlusions overlay -->
                  <div *ngFor="let rect of occlusions; let idx = index" 
                       class="absolute bg-[#2563EB]/85 border-2 border-white flex items-center justify-center text-white font-extrabold text-xs shadow-md rounded-xl cursor-default group"
                       [style.left.%]="rect.x"
                       [style.top.%]="rect.y"
                       [style.width.%]="rect.w"
                       [style.height.%]="rect.h"
                       (mousedown)="$event.stopPropagation()">
                    <span class="bg-white text-[#2563EB] w-5 h-5 rounded-full flex items-center justify-center border border-[#2563EB] shadow text-[10px] font-black">{{ idx + 1 }}</span>
                    <button type="button" 
                            class="absolute -top-2.5 -right-2.5 bg-red-500 hover:bg-red-600 text-white border-none w-5 h-5 rounded-full flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-all shadow"
                            (click)="removeOcclusion(idx)"
                            title="Hapus area ini">
                      <i class="pi pi-times text-[8px]"></i>
                    </button>
                  </div>

                  <!-- Selection box drawing outline -->
                  <div *ngIf="isDrawing" 
                       class="absolute bg-[#2563EB]/30 border-2 border-dashed border-[#2563EB] rounded-lg"
                       [style.left.px]="drawRect.x"
                       [style.top.px]="drawRect.y"
                       [style.width.px]="drawRect.w"
                       [style.height.px]="drawRect.h">
                  </div>
                </div>

                <div *ngIf="!previewImageSrc" class="w-full min-h-[350px] border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center text-slate-400 bg-slate-50 p-6 text-center">
                  <i class="pi pi-image text-5xl mb-3 text-slate-300"></i>
                  <span class="font-bold text-slate-700 mb-1">Belum Ada Gambar Diagram</span>
                  <span class="text-xs max-w-xs leading-relaxed text-slate-400">Silakan unggah file gambar diagram pada panel kiri untuk mulai menggambar area penutup.</span>
                </div>
              </div>

            </div>

            <!-- 3. Additional Settings Bar (Link YouTube & Status Aktif) -->
            <div class="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div class="flex-1 w-full flex items-center gap-2.5">
                <i class="pi pi-youtube text-red-500 text-lg"></i>
                <div class="flex-1">
                  <input pInputText id="youtube_link" formControlName="youtube_link" class="w-full p-inputtext-sm" placeholder="Link YouTube Tambahan (Opsional)... Contoh: https://youtu.be/..." />
                </div>
              </div>
              <div class="flex items-center gap-2.5 shrink-0 pl-3 border-t sm:border-t-0 sm:border-l border-slate-200/80 pt-2 sm:pt-0">
                <span class="text-xs font-bold text-slate-700">Status Aktif</span>
                <p-toggleswitch id="is_active" formControlName="is_active"></p-toggleswitch>
              </div>
            </div>

          </form>
        </ng-template>

        <ng-template pTemplate="footer">
          <div class="flex items-center justify-end gap-2.5 pt-2">
            <p-button label="Batal" icon="pi pi-times" [text]="true" severity="secondary" (onClick)="hideDialog()"></p-button>
            <p-button label="Simpan Perubahan" icon="pi pi-check" severity="primary" (onClick)="onSubmit()" [disabled]="!flashcardForm.valid"></p-button>
          </div>
        </ng-template>
      </p-dialog>
    </div>
  `,
  providers: [MessageService, ConfirmationService]
})
export class FlashcardComponent implements OnInit {
  flashcards: Flashcard[] = [];
  categories: FlashcardCategory[] = [];
  first: number = 0;
  totalRecords: number = 0;
  rows: number = 10;
  currentPage: number = 1;
  loadingTable: boolean = false;
  searchKeyword: string = '';
  searchTimeout: any;
  isLoadingForm: boolean = false;
  private _selectedFilterCategoryId: any = null;
  get selectedFilterCategoryId(): any {
    return this._selectedFilterCategoryId;
  }
  set selectedFilterCategoryId(val: any) {
    this._selectedFilterCategoryId = val;
    this.first = 0;
    this.loadFlashcards(1);
  }

  get displayFlashcards(): Flashcard[] {
    return this.flashcards;
  }

  getCleanSnippet(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    const clean = tmp.textContent || tmp.innerText || '';
    return clean.replace(/\s+/g, ' ').trim();
  }

  getCategoryCardCount(categoryName: string): number {
    if (!categoryName) return 0;
    const match = this.flashcards.find(f => f.category_name === categoryName);
    if (match && match.category_card_count) {
      return match.category_card_count;
    }
    return this.displayFlashcards.filter(f => f.category_name === categoryName).length;
  }
  displayDialog: boolean = false;
  flashcardForm!: FormGroup;
  isEditMode: boolean = false;
  editingFlashcard: Flashcard | null = null;
  selectedFiles: File[] = [];
  selectedQuestionImageFiles: File[] = [];
  deletedImageIds: string[] = [];

  // Occlusion drawing board properties
  occlusions: any[] = [];
  previewImageSrc: string = '';
  isDrawing: boolean = false;
  startX: number = 0;
  startY: number = 0;
  imgWidth: number = 0;
  imgHeight: number = 0;
  drawRect = { x: 0, y: 0, w: 0, h: 0 };

  @ViewChild('imageUpload') imageUpload: any;
  @ViewChild('questionImageUpload') questionImageUpload: any;

  constructor(
    private flashcardService: FlashcardService,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.loadFlashcards(this.currentPage);
    this.loadCategories();
    this.flashcardForm = this.fb.group({
      text: ['', Validators.required],
      description: ['', Validators.required],
      topic: [''],
      type: ['text', Validators.required],
      youtube_link: [''],
      flashcard_category_id: [null, Validators.required],
      is_active: [true]
    });

    // Reset occlusion variables when type changes
    this.flashcardForm.get('type')?.valueChanges.subscribe(val => {
      if (this.isLoadingForm) return;
      this.occlusions = [];
      this.previewImageSrc = '';
      if (val === 'image') {
        this.flashcardForm.patchValue({
          description: '',
          text: 'Tebak bagian-bagian gambar berikut'
        });
      } else {
        this.flashcardForm.patchValue({
          description: '',
          text: ''
        });
      }
    });
  }

  loadFlashcards(page: number = 1): void {
    this.loadingTable = true;
    this.currentPage = page;
    this.flashcardService.getFlashcardsPaginated(
      page,
      this.rows,
      this.searchKeyword,
      this.selectedFilterCategoryId
    ).subscribe({
      next: (response: any) => {
        this.loadingTable = false;
        const data: Flashcard[] = response?.data || [];
        this.totalRecords = response?.total !== undefined ? response.total : data.length;
        this.flashcards = data.map(f => ({
          ...f,
          is_active: (f.is_active == 1 || f.is_active === '1' || (f.is_active as any) === true) ? 1 : 0
        }));
      },
      error: (err) => {
        this.loadingTable = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal memuat flashcard' });
        console.error('Error loading flashcards:', err);
      }
    });
  }

  onLazyLoad(event: any): void {
    const page = event.first !== undefined && event.rows ? Math.floor(event.first / event.rows) + 1 : 1;
    this.rows = event.rows || 10;
    this.first = event.first || 0;
    this.loadFlashcards(page);
  }

  loadCategories(): void {
    this.flashcardService.getCategories().subscribe({
      next: (data) => {
        this.categories = data;
      },
      error: (err) => {
        console.error('Error loading categories:', err);
      }
    });
  }

  getImageUrl(imagePath: string): string {
    if (!imagePath) return '';
    return `https://oscemediko.id/api-timer/${imagePath}`;
  }

  getFormattedDescription(flashcard: Flashcard): string {
    if (!flashcard.description) return '-';
    if (flashcard.type === 'image') {
      try {
        const occlusions = JSON.parse(flashcard.description);
        if (Array.isArray(occlusions) && occlusions.length > 0) {
          return occlusions.map((rect, idx) => `<b>Area ${idx + 1}:</b> ${rect.label || '-'}`).join('<br>');
        }
        return '-';
      } catch (e) {
        return flashcard.description;
      }
    }
    return flashcard.description;
  }

  getExistingQuestionImages(): { id?: string; image_path: string }[] {
    if (!this.editingFlashcard) return [];
    if (this.editingFlashcard.question_images && this.editingFlashcard.question_images.length > 0) {
      return this.editingFlashcard.question_images;
    }
    if (this.editingFlashcard.question_image_path) {
      return [{ image_path: this.editingFlashcard.question_image_path }];
    }
    return [];
  }

  getExistingAnswerImages(): { id?: string; image_path: string }[] {
    if (!this.editingFlashcard) return [];
    if (this.editingFlashcard.images && this.editingFlashcard.images.length > 0) {
      return this.editingFlashcard.images;
    }
    if (this.editingFlashcard.image_path) {
      return [{ image_path: this.editingFlashcard.image_path }];
    }
    return [];
  }

  deleteExistingQuestionImage(img: { id?: string; image_path: string }, index: number): void {
    this.confirmationService.confirm({
      message: 'Apakah Anda yakin ingin menghapus gambar pertanyaan ini?',
      header: 'Konfirmasi Hapus',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (img.id) {
          this.flashcardService.deleteFlashcardImage(img.id).subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Gambar berhasil dihapus' });
              if (this.editingFlashcard && this.editingFlashcard.question_images) {
                this.editingFlashcard.question_images.splice(index, 1);
              }
              this.loadFlashcards(this.currentPage);
            },
            error: (err) => {
              console.error('Error deleting image:', err);
              this.deletedImageIds.push(img.id!);
              if (this.editingFlashcard && this.editingFlashcard.question_images) {
                this.editingFlashcard.question_images.splice(index, 1);
              }
              this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Gambar ditandai untuk dihapus' });
            }
          });
        } else {
          if (this.editingFlashcard) {
            this.editingFlashcard.question_image_path = '';
          }
          this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Gambar ditandai untuk dihapus' });
        }
      }
    });
  }

  deleteExistingAnswerImage(img: { id?: string; image_path: string }, index: number): void {
    this.confirmationService.confirm({
      message: 'Apakah Anda yakin ingin menghapus gambar jawaban ini?',
      header: 'Konfirmasi Hapus',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        if (img.id) {
          this.flashcardService.deleteFlashcardImage(img.id).subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Gambar berhasil dihapus' });
              if (this.editingFlashcard && this.editingFlashcard.images) {
                this.editingFlashcard.images.splice(index, 1);
              }
              this.loadFlashcards(this.currentPage);
            },
            error: (err) => {
              console.error('Error deleting image:', err);
              this.deletedImageIds.push(img.id!);
              if (this.editingFlashcard && this.editingFlashcard.images) {
                this.editingFlashcard.images.splice(index, 1);
              }
              this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Gambar ditandai untuk dihapus' });
            }
          });
        } else {
          if (this.editingFlashcard) {
            this.editingFlashcard.image_path = '';
          }
          this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Gambar ditandai untuk dihapus' });
        }
      }
    });
  }

  addFlashcard(): void {
    this.isLoadingForm = true;
    this.isEditMode = false;
    this.editingFlashcard = null;
    this.selectedFiles = [];
    this.selectedQuestionImageFiles = [];
    this.deletedImageIds = [];
    this.flashcardForm.reset({ is_active: true, type: 'text' });
    this.displayDialog = true;
    setTimeout(() => this.clearFileUploads());
    this.isLoadingForm = false;
  }

  editFlashcard(flashcard: Flashcard): void {
    this.isLoadingForm = true;
    this.isEditMode = true;
    this.editingFlashcard = { ...flashcard };
    
    // Deep clone question_images and images to avoid modifying the original list directly before saving
    if (flashcard.question_images) {
      this.editingFlashcard.question_images = [...flashcard.question_images];
    }
    if (flashcard.images) {
      this.editingFlashcard.images = [...flashcard.images];
    }

    this.selectedFiles = [];
    this.selectedQuestionImageFiles = [];
    this.deletedImageIds = [];

    if (flashcard.type === 'image') {
      try {
        this.occlusions = JSON.parse(flashcard.description) || [];
      } catch (e) {
        this.occlusions = [];
      }
      const img = this.getExistingQuestionImages();
      this.previewImageSrc = img.length > 0 ? this.getImageUrl(img[0].image_path) : '';
    } else {
      this.occlusions = [];
      this.previewImageSrc = '';
    }

    this.displayDialog = true;

    // Patch form values inside a setTimeout to let the DOM/p-editor render and initialize first
    setTimeout(() => {
      this.flashcardForm.patchValue({
        text: flashcard.text,
        description: flashcard.description,
        topic: flashcard.topic || '',
        type: flashcard.type || 'text',
        youtube_link: flashcard.youtube_link || '',
        flashcard_category_id: flashcard.flashcard_category_id,
        is_active: flashcard.is_active === 1
      });
      this.clearFileUploads();
      this.isLoadingForm = false;
    });
  }

  hideDialog(): void {
    this.isLoadingForm = true;
    this.displayDialog = false;
    this.isEditMode = false;
    this.editingFlashcard = null;
    this.selectedFiles = [];
    this.selectedQuestionImageFiles = [];
    this.deletedImageIds = [];
    this.occlusions = [];
    this.previewImageSrc = '';
    this.flashcardForm.reset();
    this.clearFileUploads();
    this.isLoadingForm = false;
  }

  clearFileUploads(): void {
    if (this.imageUpload) this.imageUpload.clear();
    if (this.questionImageUpload) this.questionImageUpload.clear();
  }

  onFileSelect(event: any): void {
    const files = Array.from(event.files) as File[];
    files.forEach(f => {
      if (!this.selectedFiles.some(existing => existing.name === f.name && existing.size === f.size)) {
        this.selectedFiles.push(f);
      }
    });
  }

  onQuestionImageSelect(event: any): void {
    const files = Array.from(event.files) as File[];
    files.forEach(f => {
      if (!this.selectedQuestionImageFiles.some(existing => existing.name === f.name && existing.size === f.size)) {
        this.selectedQuestionImageFiles.push(f);
      }
    });

    if (files.length > 0 && this.flashcardForm.get('type')?.value === 'image') {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewImageSrc = e.target.result;
        this.occlusions = [];
        this.updateFormValidityAndData();
      };
      reader.readAsDataURL(files[0]);
    }
  }

  removeQuestionFile(index: number): void {
    this.selectedQuestionImageFiles.splice(index, 1);
    if (this.selectedQuestionImageFiles.length === 0) {
      const img = this.getExistingQuestionImages();
      this.previewImageSrc = img.length > 0 ? this.getImageUrl(img[0].image_path) : '';
      if (!this.previewImageSrc) {
        this.occlusions = [];
        this.updateFormValidityAndData();
      }
    }
  }

  removeAnswerFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  startSelect(event: MouseEvent): void {
    event.preventDefault();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.startX = event.clientX - rect.left;
    this.startY = event.clientY - rect.top;
    this.imgWidth = rect.width;
    this.imgHeight = rect.height;
    
    this.isDrawing = true;
    this.drawRect = { x: this.startX, y: this.startY, w: 0, h: 0 };

    const mousemove = (moveEvent: MouseEvent) => {
      if (!this.isDrawing) return;
      const currentX = moveEvent.clientX - rect.left;
      const currentY = moveEvent.clientY - rect.top;
      
      const x = Math.min(this.startX, currentX);
      const y = Math.min(this.startY, currentY);
      const w = Math.abs(this.startX - currentX);
      const h = Math.abs(this.startY - currentY);
      
      this.drawRect = { x, y, w, h };
    };

    const mouseup = () => {
      this.isDrawing = false;
      window.removeEventListener('mousemove', mousemove);
      window.removeEventListener('mouseup', mouseup);

      if (this.drawRect.w > 8 && this.drawRect.h > 8) {
        const pctX = (this.drawRect.x / this.imgWidth) * 100;
        const pctY = (this.drawRect.y / this.imgHeight) * 100;
        const pctW = (this.drawRect.w / this.imgWidth) * 100;
        const pctH = (this.drawRect.h / this.imgHeight) * 100;

        this.occlusions.push({
          id: Date.now(),
          x: pctX,
          y: pctY,
          w: pctW,
          h: pctH,
          label: ''
        });

        this.updateFormValidityAndData();
      }
    };

    window.addEventListener('mousemove', mousemove);
    window.addEventListener('mouseup', mouseup);
  }

  removeOcclusion(index: number): void {
    this.occlusions.splice(index, 1);
    this.updateFormValidityAndData();
  }

  updateFormValidityAndData(): void {
    if (this.flashcardForm.get('type')?.value === 'image') {
      const descriptionVal = this.occlusions.length > 0 ? JSON.stringify(this.occlusions) : '';
      this.flashcardForm.patchValue({
        description: descriptionVal
      });
      this.flashcardForm.get('description')?.updateValueAndValidity();
    }
  }

  onSubmit(): void {
    if (this.flashcardForm.valid) {
      const formData = new FormData();
      const formValue = this.flashcardForm.value;

      // Convert boolean to number for is_active
      formValue.is_active = formValue.is_active ? 1 : 0;

      // Ensure youtube_link is not null/undefined
      if (!formValue.youtube_link) {
        formValue.youtube_link = '';
      }

      // Ensure topic is not null/undefined
      if (!formValue.topic) {
        formValue.topic = '';
      }

      // Add form fields to FormData
      Object.keys(formValue).forEach(key => {
        if (key === 'description' && formValue.type === 'image') {
          formData.append(key, JSON.stringify(this.occlusions));
        } else {
          formData.append(key, formValue[key]);
        }
      });

      // Add files if selected
      if (this.selectedFiles.length > 0) {
        this.selectedFiles.forEach(file => {
          formData.append('images[]', file);
        });
      }

      // Add question images if selected
      if (this.selectedQuestionImageFiles.length > 0) {
        this.selectedQuestionImageFiles.forEach(file => {
          formData.append('question_images[]', file);
        });
      }

      // Add deleted image IDs if any
      if (this.deletedImageIds.length > 0) {
        this.deletedImageIds.forEach(id => {
          formData.append('deleted_image_ids[]', id);
        });
      }

      // Handle legacy single-image clearing
      if (this.isEditMode && this.editingFlashcard) {
        if (!this.editingFlashcard.question_image_path) {
          formData.append('question_image_path', '');
        }
        if (!this.editingFlashcard.image_path) {
          formData.append('image_path', '');
        }
      }

      // Add ID for update
      if (this.isEditMode && this.editingFlashcard) {
        formData.append('id', this.editingFlashcard.id!);
        if (this.editingFlashcard.urutan !== undefined) {
          formData.append('urutan', this.editingFlashcard.urutan.toString());
        }
      } else {
        formData.append('urutan', (this.flashcards.length + 1).toString());
      }

      if (this.isEditMode && this.editingFlashcard) {
        this.flashcardService.updateFlashcard(formData).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Flashcard berhasil diperbarui' });
            this.hideDialog();
            this.loadFlashcards(this.currentPage);
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal memperbarui flashcard' });
            console.error('Error updating flashcard:', err);
          }
        });
      } else {
        this.flashcardService.addFlashcard(formData).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Flashcard berhasil dibuat' });
            this.hideDialog();
            this.loadFlashcards(this.currentPage);
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal membuat flashcard' });
            console.error('Error creating flashcard:', err);
          }
        });
      }
    }
  }

  deleteFlashcard(flashcard: Flashcard): void {
    this.confirmationService.confirm({
      message: 'Apakah Anda yakin ingin menghapus flashcard "' + flashcard.text + '"?',
      header: 'Konfirmasi',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.flashcardService.deleteFlashcard(flashcard.id!).subscribe({
          next: () => {
            this.messageService.add({severity:'success', summary: 'Berhasil', detail: 'Flashcard Dihapus', life: 3000});
            this.loadFlashcards(this.currentPage);
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal menghapus flashcard' });
            console.error('Error deleting flashcard:', err);
          }
        });
      }
    });
  }

  onRowReorder(event: any): void {
    const requests = this.flashcards.map((item, index) => {
      const formData = new FormData();
      formData.append('id', item.id!);
      formData.append('text', item.text);
      formData.append('description', item.description);
      formData.append('topic', item.topic || '');
      formData.append('type', item.type || 'text');
      formData.append('youtube_link', item.youtube_link || '');
      formData.append('flashcard_category_id', item.flashcard_category_id.toString());
      formData.append('is_active', (item.is_active === 1 || item.is_active === '1' || (item.is_active as any) === true) ? '1' : '0');
      formData.append('urutan', (index + 1).toString());
      return this.flashcardService.updateFlashcard(formData);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Berhasil', detail: 'Urutan flashcard berhasil diperbarui' });
        this.loadFlashcards(this.currentPage);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Gagal memperbarui urutan' });
        console.error('Error reordering flashcards:', err);
      }
    });
  }

  onInput(event: Event, dt?: any): void {
    const inputElement = event.target as HTMLInputElement;
    this.searchKeyword = inputElement.value || '';
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.first = 0;
      this.loadFlashcards(1);
    }, 350);
  }
}
